import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BudgetRoundModel, CommodityModel, CounterpartyModel, LocationModel, PortfolioModel, TierModel, GetCustomerDataResponse, GetCustomerSettingsResponse, GetCustomerReportsResponse, CustomerReportModel } from '../../Generated/Commodity-Management-Api';
import { LoadingState } from '../../Types';

interface CommodityManagementState {
  customerData?: GetCustomerDataResponse;
  customerSettings?: GetCustomerSettingsResponse;
  customerReports?: CustomerReportModel[];
  siteLoadingState: LoadingState;
}

interface SetCommodityManagementPayload {
  customerData: GetCustomerDataResponse;
  customerSettings: GetCustomerSettingsResponse;
  customerReports: CustomerReportModel[];
}

interface SetCustomerReportUserOverridePayload {
  customerReportId: number;
  userOverrides: string | null | undefined;
}

interface UpdateCustomerReportPayload {
  customerReportId: number;
  reportName: string;
  configuration: string;
}

interface RemoveCustomerReportPayload {
  customerReportId: number;
}

interface AddCustomerReportPayload {
  report: CustomerReportModel;
}

interface AddCounterpartyPayload {
  counterparty: CounterpartyModel;
}

interface UpdateCounterpartyPayload {
  counterparty: CounterpartyModel;
}

interface RemoveCounterpartyPayload {
  counterpartyId: number;
}

interface AddPortfolioPayload {
  portfolio: PortfolioModel;
}

interface UpdatePortfolioPayload {
  portfolio: PortfolioModel;
}

interface RemovePortfolioPayload {
  portfolioId: number;
}

interface AddCommodityPayload {
  commodity: CommodityModel;
}

interface UpdateCommodityPayload {
  commodity: CommodityModel;
}

interface RemoveCommodityPayload {
  commodityId: number;
}

interface AddLocationPayload {
  location: LocationModel;
}

interface UpdateLocationPayload {
  location: LocationModel;
}

interface RemoveLocationPayload {
  locationId: number;
}

interface RemoveTierPayload {
  tierId: number;
}

interface AddTierPayload {
  tier: TierModel;
}

interface AddCustomerBudgetRoundPayload {
  budgetRound: BudgetRoundModel;
}
  
const initialState: CommodityManagementState = {
  siteLoadingState: LoadingState.None,
};

export const userAccessSlice = createSlice({
    name: 'userAccess',
    initialState,
    reducers: {
      setCommodityManagementState: (state: CommodityManagementState, { payload }: PayloadAction<SetCommodityManagementPayload>): void => {
        state.customerData = payload.customerData;
        state.customerSettings = payload.customerSettings;
        state.customerReports = payload.customerReports;
        state.siteLoadingState = LoadingState.Loaded;
      },
      setCommodityManagementLoadingState: (state: CommodityManagementState, { payload }: PayloadAction<LoadingState>): void => {
        state.siteLoadingState = payload;
      },
      resetCommodityManagementState: (state: CommodityManagementState) => {
        state.customerData = undefined;
        state.customerSettings = undefined;
        state.customerReports = undefined;
        state.siteLoadingState = LoadingState.None;
      },
      setCustomerReportUserOverride: (state: CommodityManagementState, { payload }: PayloadAction<SetCustomerReportUserOverridePayload>): void => {
        state.customerReports = state.customerReports?.map(x => {

          if (x.customerReportId == payload.customerReportId) {
            return {...x, userOverrides: payload.userOverrides}
          }

          return x;
        })
      },
      addCustomerBudgetRound: (state: CommodityManagementState, { payload }: PayloadAction<AddCustomerBudgetRoundPayload>): void => {
        if (!state.customerData) {
          return;
        }

        const existingRounds = state.customerData.budgetRounds ?? [];
        const hasRound = existingRounds.some(x => x.budgetRoundId === payload.budgetRound.budgetRoundId);

        if (hasRound) {
          return;
        }

        state.customerData.budgetRounds = [...existingRounds, payload.budgetRound];
      },
      updateCustomerReport: (state: CommodityManagementState, { payload }: PayloadAction<UpdateCustomerReportPayload>): void => {
        state.customerReports = state.customerReports?.map(x => {

          if (x.customerReportId == payload.customerReportId) {
            return {...x, reportName: payload.reportName, configuration: payload.configuration}
          }

          return x;
        })
      },
      addCustomerReport: (state: CommodityManagementState, { payload }: PayloadAction<AddCustomerReportPayload>): void => {
        const existingReports = state.customerReports ?? [];
        const alreadyExists = existingReports.some(x => x.customerReportId === payload.report.customerReportId);

        if (alreadyExists) {
          return;
        }

        state.customerReports = [...existingReports, payload.report];
      },
      removeCustomerReport: (state: CommodityManagementState, { payload }: PayloadAction<RemoveCustomerReportPayload>): void => {
        state.customerReports = state.customerReports?.filter(x => x.customerReportId !== payload.customerReportId);
      },
      setCustomerSettings: (state: CommodityManagementState, { payload }: PayloadAction<GetCustomerSettingsResponse>): void => {
        state.customerSettings = payload;
      },
      addCounterparty: (state: CommodityManagementState, { payload }: PayloadAction<AddCounterpartyPayload>): void => {
        if (!state.customerData) return;
        const existing = state.customerData.counterparties ?? [];
        if (existing.some(x => x.counterpartyId === payload.counterparty.counterpartyId)) return;
        state.customerData.counterparties = [...existing, payload.counterparty];
      },
      updateCounterparty: (state: CommodityManagementState, { payload }: PayloadAction<UpdateCounterpartyPayload>): void => {
        if (!state.customerData) return;
        state.customerData.counterparties = state.customerData.counterparties?.map(x =>
          x.counterpartyId === payload.counterparty.counterpartyId ? payload.counterparty : x
        );
      },
      removeCounterparty: (state: CommodityManagementState, { payload }: PayloadAction<RemoveCounterpartyPayload>): void => {
        if (!state.customerData) return;
        state.customerData.counterparties = state.customerData.counterparties?.filter(x => x.counterpartyId !== payload.counterpartyId);
      },
      addPortfolio: (state: CommodityManagementState, { payload }: PayloadAction<AddPortfolioPayload>): void => {
        if (!state.customerData) return;
        const existing = state.customerData.portfolios ?? [];
        if (existing.some(x => x.portfolioId === payload.portfolio.portfolioId)) return;
        state.customerData.portfolios = [...existing, payload.portfolio];
      },
      updatePortfolio: (state: CommodityManagementState, { payload }: PayloadAction<UpdatePortfolioPayload>): void => {
        if (!state.customerData) return;
        state.customerData.portfolios = state.customerData.portfolios?.map(x =>
          x.portfolioId === payload.portfolio.portfolioId ? payload.portfolio : x
        );
      },
      removePortfolio: (state: CommodityManagementState, { payload }: PayloadAction<RemovePortfolioPayload>): void => {
        if (!state.customerData) return;
        state.customerData.portfolios = state.customerData.portfolios?.filter(x => x.portfolioId !== payload.portfolioId);
      },
      addCommodity: (state: CommodityManagementState, { payload }: PayloadAction<AddCommodityPayload>): void => {
        if (!state.customerData) return;
        const existing = state.customerData.commodities ?? [];
        if (existing.some(x => x.commodityId === payload.commodity.commodityId)) return;
        state.customerData.commodities = [...existing, payload.commodity];
      },
      updateCommodity: (state: CommodityManagementState, { payload }: PayloadAction<UpdateCommodityPayload>): void => {
        if (!state.customerData) return;
        state.customerData.commodities = state.customerData.commodities?.map(x =>
          x.commodityId === payload.commodity.commodityId ? payload.commodity : x
        );
      },
      removeCommodity: (state: CommodityManagementState, { payload }: PayloadAction<RemoveCommodityPayload>): void => {
        if (!state.customerData) return;
        state.customerData.commodities = state.customerData.commodities?.filter(x => x.commodityId !== payload.commodityId);
      },
      addLocation: (state: CommodityManagementState, { payload }: PayloadAction<AddLocationPayload>): void => {
        if (!state.customerData) return;
        const existing = state.customerData.locations ?? [];
        if (existing.some(x => x.locationId === payload.location.locationId)) return;
        state.customerData.locations = [...existing, payload.location];
      },
      updateLocation: (state: CommodityManagementState, { payload }: PayloadAction<UpdateLocationPayload>): void => {
        if (!state.customerData) return;
        state.customerData.locations = state.customerData.locations?.map(x =>
          x.locationId === payload.location.locationId ? payload.location : x
        );
      },
      removeLocation: (state: CommodityManagementState, { payload }: PayloadAction<RemoveLocationPayload>): void => {
        if (!state.customerData) return;
        state.customerData.locations = state.customerData.locations?.filter(x => x.locationId !== payload.locationId);
      },
      removeTier: (state: CommodityManagementState, { payload }: PayloadAction<RemoveTierPayload>): void => {
        if (!state.customerData) return;
        state.customerData.tiers = state.customerData.tiers?.filter(x => x.tierId !== payload.tierId);
      },
      addTier: (state: CommodityManagementState, { payload }: PayloadAction<AddTierPayload>): void => {
        if (!state.customerData) return;
        const existing = state.customerData.tiers ?? [];
        if (existing.some(x => x.tierId === payload.tier.tierId)) return;
        state.customerData.tiers = [...existing, payload.tier];
      },
    }
  });
  
  export const { setCommodityManagementState, setCommodityManagementLoadingState, resetCommodityManagementState, setCustomerReportUserOverride, addCustomerBudgetRound, updateCustomerReport, addCustomerReport, removeCustomerReport, setCustomerSettings, addCounterparty, updateCounterparty, removeCounterparty, addPortfolio, updatePortfolio, removePortfolio, addCommodity, updateCommodity, removeCommodity, addLocation, updateLocation, removeLocation, removeTier, addTier } = userAccessSlice.actions;
  export default userAccessSlice.reducer;